import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useState, useEffect } from "react";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import ProfileSetup from "./pages/ProfileSetup";
import Settings from "./pages/Settings";
import MapView from "./pages/MapView";
import GiftFlow from "./pages/GiftFlow";
import ThankYou from "./pages/ThankYou";
import Dashboard from "./pages/Dashboard";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Safety from "./pages/Safety";
import NotFound from "./pages/NotFound";
import SavedUsers from "./pages/SavedUsers";
import Help from "./pages/Help";
import Support from "./pages/Support";
import ResetPassword from "./pages/ResetPassword";
import UpdatePassword from "./pages/UpdatePassword";
import StripeCallback from "./pages/StripeCallback";
import Ambassador from "./pages/Ambassador";
import AdminAmbassadors from "./pages/AdminAmbassadors";
import PrizeDraw from "./pages/PrizeDraw";
import PrizeDrawConfirmation from "./pages/PrizeDrawConfirmation";
import { isMobileApp } from "@/lib/platform";
import ProtectedRoute from "@/components/ProtectedRoute";
import useAuth from "@/hooks/useAuth";
import useAutoCreateUser from "@/hooks/useAutoCreateUser";
import { MobileOnlyScreen } from "@/components/MobileOnlyScreen";
import { LocationSharingProvider } from "@/contexts/LocationSharingContext";
import { InactivityProvider } from "@/contexts/InactivityContext";
import usePushNotifications from "@/hooks/usePushNotifications";
import { Capacitor } from "@capacitor/core";
import Spinner from "@/components/Spinner";

const queryClient = new QueryClient();

const AppContent = () => {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const { user } = useAuth();

  // Auto-create user row after authentication
  useAutoCreateUser(user?.id);

  useEffect(() => {
    const checkMobile = isMobileApp();
    setIsMobile(checkMobile);
  }, []);

  // Always call hooks in the same order; guard inside hook handles web
  const platform = Capacitor.getPlatform() as 'android' | 'ios' | 'web';
  usePushNotifications({ platform });

  if (isMobile === null) {
    return <Spinner size={32} />;
  }

  // Web users: Show download screen, legal pages, Stripe callback, and ambassador pages
  if (!isMobile) {
    return (
      <Routes>
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/support" element={<Support />} />
        <Route path="/stripe-callback" element={<StripeCallback />} />
        <Route path="/ambassador" element={<Ambassador />} />
        <Route path="/admin/ambassadors" element={<AdminAmbassadors />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="*" element={<MobileOnlyScreen />} />
      </Routes>
    );
  }

  // Mobile app users: Full app access
  return (
    <InactivityProvider>
      <LocationSharingProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/profile-setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/map" element={<ProtectedRoute><MapView /></ProtectedRoute>} />
          <Route path="/gift" element={<ProtectedRoute><GiftFlow /></ProtectedRoute>} />
          <Route path="/prize-draw" element={<ProtectedRoute><PrizeDraw /></ProtectedRoute>} />
          <Route path="/prize-draw-confirmation" element={<ProtectedRoute><PrizeDrawConfirmation /></ProtectedRoute>} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/saved-users" element={<ProtectedRoute><SavedUsers /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/safety" element={<Safety />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </LocationSharingProvider>
    </InactivityProvider>
  );
};


const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <SonnerToaster />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
